import * as React from "react";
import { styled } from "styled-components";
import { Image, File } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import { RichText } from "../../internal-components/RichText.js";
import { useInViewport } from "../../hooks/useInViewport.js";
import {
  ButtonAction,
  ButtonActionProps,
} from "../../internal-components/Button.js";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { useImageProps } from "../../hooks/useImageProps.js";
import { createEditableProps } from "@ludens-reklame/rubics-v3-react/util/fns";
import type { BackgroundColor } from "../../types/theme.js";
import { surfaceColorMap } from "../../util/styles.js";
import { play } from "../../icons/index.js";

export interface VideoBannerProps {
  video?: File;
  videoUrl?: string;
  videoEmbed?: string;
  text?: string;
  actions?: ButtonActionProps[];
  image?: Image;
  backgroundColor?: BackgroundColor;
  position?: "left" | "right";
}

export const VideoBanner: React.FunctionComponent<VideoBannerProps> = ({
  video,
  videoUrl,
  videoEmbed,
  image,
  text = "",
  actions = [],
  backgroundColor = "surface2",
  position = "right",
}) => {
  const ref = useInViewport();
  const [isPlaying, setIsPlaying] = React.useState(false);

  const activeActions = React.useMemo(
    () => (actions || []).filter((x) => x?.link) as ButtonActionProps[],
    [actions]
  );

  const imageProps = useImageProps(image, {
    width: 720,
    fit: "cover",
  });

  const embedUrl = videoUrl?.includes("watch?v=")
    ? videoUrl.replace("watch?v=", "embed/")
    : videoUrl;

  const autoplayEmbedUrl = embedUrl
    ? `${embedUrl}${embedUrl.includes("?") ? "&" : "?"}autoplay=1`
    : undefined;

  const handlePlay = () => {
    setIsPlaying(true);
  };

  return (
    <VerticalSpacer>
      <HorizontalSpacer desktopGutter="xs">
        <Constrainer>
          <BannerBackground
            ref={ref}
            backgroundColor={backgroundColor}
            position={position}
          >
            <BannerWrapper position={position}>
              <BannerImageWrapper
                position={position}
                onClick={!isPlaying ? handlePlay : undefined}
                isPlaying={isPlaying}
              >
                {isPlaying ? (
                  video ? (
                    <video
                      src={video.url}
                      controls
                      autoPlay
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    >
                      Your browser does not support the video tag.
                    </video>
                  ) : autoplayEmbedUrl ? (
                    <iframe
                      src={autoplayEmbedUrl}
                      style={{ border: "none", width: "100%", height: "100%" }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  ) : videoEmbed ? (
                    <div
                      className="embed"
                      dangerouslySetInnerHTML={{ __html: videoEmbed }}
                      style={{ width: "100%", height: "100%" }}
                    />
                  ) : null
                ) : (
                  <>
                    {imageProps && <img {...imageProps} />}
                    <PlayButton aria-label="Spill av video">{play}</PlayButton>
                  </>
                )}
              </BannerImageWrapper>
              <BannerContentWrapper position={position}>
                <BannerRichText
                  dangerouslySetInnerHTML={{ __html: text }}
                  {...createEditableProps("html", "text")}
                />
                {activeActions.length > 0 && (
                  <BannerActions>
                    {activeActions.map((action, i) => (
                      <ButtonAction key={i} {...action} onClick={handlePlay} />
                    ))}
                  </BannerActions>
                )}
              </BannerContentWrapper>
            </BannerWrapper>
          </BannerBackground>
        </Constrainer>
      </HorizontalSpacer>
    </VerticalSpacer>
  );
};

export interface StyleProps {
  position?: "left" | "right";
  backgroundColor?: BackgroundColor;
  isPlaying?: boolean;
}


export const BannerBackground = styled.div<StyleProps>`
  background: ${({ backgroundColor }) =>
    backgroundColor ? surfaceColorMap[backgroundColor] : "var(--videobanner-background)"};
  color: ${({ backgroundColor }) => backgroundColor === "surface3" ? "#fff" : "var(--swatch2)"};
  ${({ backgroundColor }) =>
    backgroundColor === "surface3" &&
    `
    --videobanner-title-color: #fff;
    --videobanner-description-color: #fff;
  `}
  border-radius: ${({ theme }) => theme.borderRadius};
  overflow: hidden;
`;

export const BannerWrapper = styled.div<StyleProps>`
  display: flex;
  flex-direction: column;

  @media (min-width: ${mobileBreakPointCSS}) {
    flex-direction: ${({ position }) =>
      position === "left" ? "row" : "row-reverse"};
  }
`;

export const BannerRichText = styled(RichText)`
  h1,
  h2,
  h3,
  h4 {
    margin: 0 0 ${(props) => props.theme.spacing.s};
    color: var(--videobanner-title-color);
  }

  p {
    margin: 0;
    color: var(--videobanner-description-color);
  }

  a {
    color: inherit;
    box-shadow: none;
  }
`;

export const BannerImageWrapper = styled.div<StyleProps>`
  position: relative;
  overflow: hidden;
  cursor: pointer;

  img {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
  }

  iframe,
  video,
  .embed,
  .embed iframe {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
  }

  @media (min-width: ${mobileBreakPointCSS}) {
    width: 50%;
    align-self: stretch;

    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      aspect-ratio: unset;
    }

    iframe,
    video,
    .embed,
    .embed iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      aspect-ratio: unset;
    }
  }
`;

export const BannerContentWrapper = styled.div<StyleProps>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing.m};
  padding: ${(props) => props.theme.spacing.l};
  justify-content: center;

  @media (min-width: ${mobileBreakPointCSS}) {
    flex: 1;
  }
`;

export const PlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  background: none;
  border: none;
  cursor: pointer;
  pointer-events: none;

  svg path {
    fill: #fff;
  }
`;

export const BannerActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing.m};
`;
